import { useId, useRef, useState, type CSSProperties } from "react";

import { Dialog } from "~/components/Dialog";
import { mobileStrings } from "~/components/mobile/strings";
import { openExternal, platform } from "~/lib/platform";

export const SHEET_PANEL_STYLE: CSSProperties = {
  width: "100%",
  maxWidth: "100%",
  borderBottomLeftRadius: 0,
  borderBottomRightRadius: 0,
  paddingBottom: "env(safe-area-inset-bottom, 0px)",
};

export function DeleteAccountSheet({
  username,
  url,
  onClose,
}: {
  username: string;
  url: string;
  onClose: () => void;
}) {
  const [typed, setTyped] = useState("");
  const [pending, setPending] = useState(false);
  const [failed, setFailed] = useState(false);
  const [attemptedInTab, setAttemptedInTab] = useState(false);
  const titleId = useId();
  const bodyId = useId();
  const inputId = useId();
  const errorId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const continueRef = useRef<HTMLAnchorElement>(null);
  const strings = mobileStrings.you.deleteSheet;

  const trimmed = typed.trim();
  const matches = trimmed.length > 0 && trimmed.toLowerCase() === username.toLowerCase();

  const openInNative = () => {
    if (!matches || pending) return;
    setFailed(false);
    setPending(true);
    void openExternal(url).then(
      () => onClose(),
      () => {
        setPending(false);
        setFailed(true);
        inputRef.current?.focus();
      },
    );
  };

  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!matches) return;
    if (platform === "tauri") {
      openInNative();
      return;
    }
    setAttemptedInTab(true);
    // Enter must fire the anchor from the gesture so a null window.open isn't read as blocked.
    continueRef.current?.click();
  };

  const confirmLabel = pending ? strings.pending : strings.confirm;

  return (
    <Dialog
      align="bottom"
      labelledBy={titleId}
      describedBy={bodyId}
      onClose={onClose}
      panelStyle={SHEET_PANEL_STYLE}
    >
      <form className="flex flex-col gap-6 px-5 py-5" onSubmit={onSubmit}>
        <div className="space-y-2">
          <h2 id={titleId} className="text-base font-light text-foreground/90">
            {strings.title}
          </h2>
          <p id={bodyId} className="text-sm font-light leading-6 text-muted-foreground">
            {strings.body}
          </p>
        </div>
        <div className="flex flex-col gap-2">
          <label htmlFor={inputId} className="text-sm font-light text-muted-foreground">
            {strings.label(username)}
          </label>
          <input
            id={inputId}
            ref={inputRef}
            value={typed}
            onChange={(event) => {
              setTyped(event.target.value);
              setFailed(false);
            }}
            aria-describedby={failed ? errorId : undefined}
            autoCapitalize="none"
            autoCorrect="off"
            autoComplete="off"
            spellCheck={false}
            className="h-11 w-full rounded-none border-0 border-b border-foreground/20 bg-transparent px-0 text-base font-light text-foreground/90 outline-none transition-colors focus:border-secondary"
          />
        </div>
        <div className="flex flex-col gap-3">
          <button
            type="button"
            onClick={onClose}
            className="h-11 rounded-lg bg-foreground text-sm font-medium text-background outline-none transition-opacity hover:opacity-80 focus-visible:shadow-neon-cyan"
          >
            {strings.cancel}
          </button>
          {platform === "tauri" ? (
            <button
              type="submit"
              data-cy="delete-account-continue"
              disabled={!matches || pending}
              className="h-11 text-sm font-light text-destructive-text outline-none transition-opacity disabled:opacity-40 focus-visible:shadow-neon-cyan"
            >
              {confirmLabel}
            </button>
          ) : (
            <a
              ref={continueRef}
              data-cy="delete-account-continue"
              href={matches ? url : undefined}
              target="_blank"
              rel="noopener noreferrer"
              tabIndex={matches ? 0 : -1}
              aria-disabled={matches ? undefined : true}
              onClick={() => {
                if (matches) setAttemptedInTab(true);
              }}
              className={`flex h-11 w-full items-center justify-center text-sm font-light text-destructive-text outline-none transition-opacity focus-visible:shadow-neon-cyan ${
                matches ? "" : "pointer-events-none opacity-40"
              }`}
            >
              {strings.confirm}
            </a>
          )}
        </div>
        {failed && (
          <p
            id={errorId}
            role="alert"
            data-cy="delete-account-error"
            className="text-sm font-light text-destructive-text"
          >
            {strings.error}
          </p>
        )}
        {platform !== "tauri" && attemptedInTab && (
          <p className="text-sm font-light text-muted-foreground">{strings.openedNote}</p>
        )}
        <p className="text-xs font-light text-muted-quiet">{strings.afterDelete}</p>
      </form>
    </Dialog>
  );
}
